import React, { Suspense, lazy, useEffect, useState } from 'react';
import { LocateFixed, MapPin, Plus, Search } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientsAPI, sitesAPI } from '../../services/api';
import { notify } from '../../utils/notify';
import Modal from '../../components/Modal';
import { inSuriname, parseLocation } from '../../utils/parseLocation';
import { searchAddress } from '../../utils/addressSearch';
import { readLocation } from '../portal/location';

const IMPRECISE_M = 100; // as checkin_max_accuracy_m
const LOCATION_ERRORS = {
  denied: 'The location was not allowed: allow it for this site in the browser, or place the point another way.',
  unavailable: 'The location is not available on this device: place the point another way.',
  timeout: 'The location took too long: try again outside, or place the point another way.',
};

// A site (post) of a client, new or existing: name, address, the point on
// the map (or typed), the geofence radius, how many guards it needs and
// notes. The map loads only with this dialog.

// Leaflet and the tiles only when a site dialog opens
const SiteMap = lazy(() => import('../../components/SiteMap'));

// Without the map (its code did not load) the coordinates can still be typed
class MapBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export const EMPTY_SITE = { name: '', address: '', latitude: '', longitude: '', geofence_radius_m: 150, guards_needed: 1, notes: '' };

const osmLink = (lat, lng) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`;

// The server's field messages ({ field: 'required' }) as one line
const fieldErrors = (error) => {
  const errors = error.response?.data?.errors;
  return errors ? Object.entries(errors).map(([field, msg]) => `${field}: ${msg}`).join('; ') : null;
};

const SiteDialog = ({ initial, canCreateClient = false, onClose, onSaved }) => {
  const { t, language } = useLanguage();
  const [siteForm, setSiteForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const saveSite = async (e) => {
    e.preventDefault();
    const { id, client_id: clientId, ...fields } = siteForm;
    const data = {
      ...fields,
      latitude: fields.latitude === '' ? null : fields.latitude,
      longitude: fields.longitude === '' ? null : fields.longitude,
    };
    // left empty: the server keeps the default (150 m, 1 guard) or the value it has
    ['geofence_radius_m', 'guards_needed'].forEach((key) => {
      if (fields[key] === '' || fields[key] == null) delete data[key];
      else data[key] = Number(fields[key]);
    });
    try {
      setSaving(true);
      if (id) await sitesAPI.update(id, data);
      else await sitesAPI.create({ ...data, client_id: clientId });
      notify(id ? t('Site updated') : t('Site created'), 'success');
      onSaved(clientId || siteForm.client_id);
    } catch (error) {
      notify(fieldErrors(error) || error.response?.data?.message || t('Error saving the site'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // the client of a new site: chosen here (or the one it was opened from);
  // a client that does not exist yet is made here too, by its name only
  const isNew = !siteForm.id;
  const [clients, setClients] = useState(null);
  const [newClient, setNewClient] = useState(null); // the name being typed, or null
  const [creatingClient, setCreatingClient] = useState(false);
  useEffect(() => {
    if (!isNew) return undefined;
    let cancelled = false;
    clientsAPI.list({ status: 'active' })
      .then((res) => { if (!cancelled) setClients(res.data.clients); })
      .catch(() => { if (!cancelled) setClients([]); });
    return () => { cancelled = true; };
  }, [isNew]);
  const addClient = async () => {
    const name = (newClient || '').trim();
    if (!name) return;
    try {
      setCreatingClient(true);
      const res = await clientsAPI.create({ company_name: name });
      setClients((list) => [...(list || []), { id: res.data.id, company_name: name }]
        .sort((a, b) => a.company_name.localeCompare(b.company_name)));
      setSiteForm((form) => ({ ...form, client_id: res.data.id }));
      setNewClient(null);
      notify(t('Client created'), 'success');
    } catch (error) {
      notify(fieldErrors(error) || error.response?.data?.message || t('Error saving the client'), 'error');
    } finally {
      setCreatingClient(false);
    }
  };

  // one place for the point: a pasted Google Maps / OpenStreetMap link or
  // "lat, lng"; the map, the search and the phone's location also set it
  const [pasted, setPasted] = useState('');
  const [pointNote, setPointNote] = useState(null); // { kind: 'ok'|'warn'|'error', text }
  const setPoint = (lat, lng) => setSiteForm((form) => ({ ...form, latitude: String(lat), longitude: String(lng) }));
  const readPasted = (value) => {
    setPasted(value);
    if (!value.trim()) { setPointNote(null); return; }
    const found = parseLocation(value);
    if (found.error === 'short_link') {
      setPointNote({ kind: 'error', text: t('Short links (maps.app.goo.gl) cannot be read here: open the link, then copy the long address from the browser, or the coordinates.') });
      return;
    }
    if (found.error) {
      setPointNote({ kind: 'error', text: t('No point found: paste a Google Maps link or coordinates such as 5.852036, -55.203828.') });
      return;
    }
    setPoint(found.lat, found.lng);
    setPointNote(inSuriname(found)
      ? { kind: 'ok', text: t('Point set: {lat}, {lng}', found) }
      : { kind: 'warn', text: t('Point set: {lat}, {lng}. It is outside Suriname: check it, or swap latitude and longitude.', found) });
  };

  // the phone's (or the computer's) location, once, when the button is tapped
  const [locating, setLocating] = useState(false);
  const locateMe = async () => {
    setLocating(true);
    const where = await readLocation();
    setLocating(false);
    if (where.location_error) {
      setPointNote({ kind: 'error', text: t(LOCATION_ERRORS[where.location_error]) });
      return;
    }
    setPoint(where.latitude, where.longitude);
    const metres = Math.round(where.accuracy_m);
    setPointNote(metres > IMPRECISE_M
      ? { kind: 'warn', text: t('Point set where you are, within {metres} m: not precise. Move the pin to the gate on the map.', { metres }) }
      : { kind: 'ok', text: t('Point set where you are, within {metres} m.', { metres }) });
  };

  // the address search (Nominatim): on Enter or the button, never as one types
  const [query, setQuery] = useState('');
  const [found, setFound] = useState(null); // null = no search yet, [] = nothing found
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const search = async () => {
    if (query.trim().length < 3 || searching) return;
    try {
      setSearching(true);
      setSearchError(false);
      setFound(await searchAddress(query, { language }));
    } catch (error) {
      setFound(null);
      setSearchError(true);
    } finally {
      setSearching(false);
    }
  };
  const choose = (place) => {
    setPoint(place.lat, place.lng);
    setSiteForm((form) => ({ ...form, address: form.address ? form.address : place.label }));
    setPointNote({ kind: 'ok', text: t('Point set: {place}', { place: place.label }) });
    setFound(null);
  };

  const siteField = (name, label, props = {}) => (
    <div className="cl-field">
      <label className="field-label" htmlFor={`site-${name}`}>{t(label)}</label>
      <input
        id={`site-${name}`}
        className="input"
        value={siteForm[name] ?? ''}
        onChange={(e) => setSiteForm({ ...siteForm, [name]: e.target.value })}
        {...props}
      />
    </div>
  );

  return (
    <Modal
      open
      title={siteForm.id ? t('Edit Site') : t('New Site')}
      onClose={onClose}
      busy={saving}
      size="md"
      // in the footer, always in view, also on a phone where the form is long
      footer={(
        <>
          <button type="button" className="btn btn--secondary" onClick={onClose}>{t('Cancel')}</button>
          <button type="submit" form="site-form" className="btn btn--primary" disabled={saving}>{saving ? t('Saving...') : t('Save')}</button>
        </>
      )}
    >
      <form id="site-form" className="cl-dialog" onSubmit={saveSite}>
        {isNew && (
          <div className="cl-field">
            <label className="field-label" htmlFor="site-client">{t('Client *')}</label>
            <div className="cl-search">
              <select id="site-client" className="select" required value={siteForm.client_id ? String(siteForm.client_id) : ''}
                onChange={(e) => setSiteForm({ ...siteForm, client_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">{clients ? t('Choose a client') : t('Loading...')}</option>
                {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
              {canCreateClient && newClient === null && (
                <button type="button" className="btn btn--secondary" onClick={() => setNewClient('')}>
                  <Plus aria-hidden="true" /> {t('New client')}
                </button>
              )}
            </div>
            {newClient !== null && (
              <div className="cl-search cl-new-client">
                <input id="site-new-client" className="input" value={newClient} maxLength={255} autoFocus
                  placeholder={t('Company name')} aria-label={t('Company name')}
                  onChange={(e) => setNewClient(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addClient(); } }} />
                <button type="button" className="btn btn--primary" onClick={addClient} disabled={creatingClient || !newClient.trim()}>{t('Add')}</button>
                <button type="button" className="btn btn--secondary" onClick={() => setNewClient(null)}>{t('Cancel')}</button>
              </div>
            )}
          </div>
        )}
        {siteField('name', 'Name *', { required: true, maxLength: 150 })}
        {siteField('address', 'Address')}
        <div className="cl-field">
          <label className="field-label" htmlFor="site-search">{t('Find the place')}</label>
          <div className="cl-search">
            <input id="site-search" className="input" type="search" value={query} autoComplete="off"
              placeholder={t('Street and district, e.g. Kwattaweg 120 Paramaribo')}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }} />
            <button type="button" className="btn btn--secondary" onClick={search} disabled={searching || query.trim().length < 3}>
              <Search aria-hidden="true" /> {searching ? t('Searching...') : t('Search')}
            </button>
          </div>
          {searchError && <p className="cl-point-note is-error" role="status">{t('The address search is not available right now: place the point on the map, paste a link or use your location.')}</p>}
          {found && found.length === 0 && <p className="cl-point-note is-warn" role="status">{t('Nothing found in Suriname. Try the street and the district, or place the point on the map.')}</p>}
          {found && found.length > 0 && (
            <ul className="cl-results">
              {found.map((place) => (
                <li key={`${place.lat},${place.lng}`}>
                  <button type="button" className="cl-result" onClick={() => choose(place)}>
                    <MapPin aria-hidden="true" /> {place.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {found && <p className="cl-osm-credit">{t('Search')}: © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors</p>}
        </div>
        <div className="cl-field">
          <label className="field-label" htmlFor="site-paste">{t('Google Maps link or coordinates')}</label>
          <input id="site-paste" className="input" value={pasted} onChange={(e) => readPasted(e.target.value)}
            placeholder={t('Paste a link, or 5.852036, -55.203828')} autoComplete="off" spellCheck="false" />
          <button type="button" className="btn btn--secondary cl-locate" onClick={locateMe} disabled={locating}>
            <LocateFixed aria-hidden="true" /> {locating ? t('Finding you...') : t('Use my location')}
          </button>
          {pointNote && <p className={`cl-point-note is-${pointNote.kind}`} role="status">{pointNote.text}</p>}
        </div>
        <MapBoundary fallback={<p className="field-hint cl-hint">{t('The map could not be loaded; type the coordinates instead.')}</p>}>
          <Suspense fallback={<p className="cl-muted cl-map-loading">{t('Loading map...')}</p>}>
            <SiteMap
              latitude={siteForm.latitude}
              longitude={siteForm.longitude}
              radius={siteForm.geofence_radius_m}
              onPick={setPoint}
            />
          </Suspense>
        </MapBoundary>
        <div className="cl-form-row">
          {siteField('latitude', 'Latitude', { type: 'number', step: 'any', min: -90, max: 90 })}
          {siteField('longitude', 'Longitude', { type: 'number', step: 'any', min: -180, max: 180 })}
        </div>
        <p className="field-hint cl-hint">
          {t('Optional: the point can be added later. Without it the site has no geofence yet.')}
          {siteForm.latitude !== '' && siteForm.longitude !== '' && (
            <> <a href={osmLink(siteForm.latitude, siteForm.longitude)} target="_blank" rel="noopener noreferrer">{t('Check on the map')}</a></>
          )}
        </p>
        <div className="cl-form-row">
          {siteField('geofence_radius_m', 'Geofence radius (m)', { type: 'number', min: 25, max: 5000, step: 1, placeholder: '150' })}
          {siteField('guards_needed', 'Guards needed', { type: 'number', min: 0, max: 50, step: 1, placeholder: '1' })}
        </div>
        <div className="cl-field">
          <label className="field-label" htmlFor="site-notes">{t('Notes')}</label>
          <textarea id="site-notes" className="textarea" rows="3" value={siteForm.notes || ''}
            onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })} />
        </div>
      </form>
    </Modal>
  );
};

export default SiteDialog;
